import React, { useState } from "react";
import { Send, CheckCircle2, AlertCircle } from "lucide-react";
import { api } from "../lib/api";
import { useLang } from "../contexts/LanguageContext";

const COPY = {
  badge: { de: "KONTAKT", en: "CONTACT", tr: "İLETİŞİM" },
  title: {
    de: "Lass uns reden.",
    en: "Let's talk.",
    tr: "Konuşalım.",
  },
  sub: {
    de: "Wir melden uns schnellstmöglich bei Ihnen.",
    en: "We'll get back to you as soon as possible.",
    tr: "En kısa sürede size geri döneceğiz.",
  },
  name: { de: "Name", en: "Name", tr: "İsim" },
  restaurant: { de: "Restaurant (optional)", en: "Restaurant (optional)", tr: "Restoran (opsiyonel)" },
  email: { de: "E-Mail", en: "Email", tr: "E-posta" },
  message: { de: "Nachricht", en: "Message", tr: "Mesaj" },
  placeholderName: { de: "Max Mustermann", en: "Jane Doe", tr: "Ad Soyad" },
  placeholderRestaurant: { de: "Café Berlin", en: "Café Berlin", tr: "Café Berlin" },
  placeholderEmail: { de: "name@beispiel.de", en: "name@example.com", tr: "ad@ornek.com" },
  placeholderMessage: {
    de: "Erzähl uns, was du brauchst — wir freuen uns auf deine Nachricht.",
    en: "Tell us what you need — we look forward to your message.",
    tr: "Neye ihtiyacın olduğunu yaz — mesajını sabırsızlıkla bekliyoruz.",
  },
  submit: { de: "Nachricht senden", en: "Send message", tr: "Mesajı gönder" },
  sending: { de: "Senden…", en: "Sending…", tr: "Gönderiliyor…" },
  success: { de: "Danke! Ihre Nachricht wurde gesendet.", en: "Thanks! Your message has been sent.", tr: "Teşekkürler! Mesajınız gönderildi." },
  successSub: {
    de: "Wir melden uns innerhalb weniger Stunden — meist sogar in Minuten.",
    en: "We'll get back within hours — usually within minutes.",
    tr: "Birkaç saat içinde — genelde dakikalar içinde — döneceğiz.",
  },
  another: { de: "Weitere Nachricht senden", en: "Send another message", tr: "Yeni mesaj gönder" },
  errGeneric: {
    de: "Etwas ist schiefgelaufen. Bitte versuche es erneut oder schreib uns direkt per E-Mail.",
    en: "Something went wrong. Please try again or email us directly.",
    tr: "Bir şeyler ters gitti. Tekrar dene ya da doğrudan e-posta gönder.",
  },
};

export default function ContactForm() {
  const { lang } = useLang();
  const tx = (k) => COPY[k]?.[lang] || COPY[k]?.de || k;

  const [form, setForm] = useState({ name: "", restaurant_name: "", email: "", message: "", website: "" });
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [err, setErr] = useState("");

  const change = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (status === "sending") return;
    setStatus("sending");
    setErr("");
    try {
      await api.post("/contact", form);
      setStatus("success");
    } catch (e2) {
      const d = e2.response?.data?.detail;
      const msg = typeof d === "string"
        ? d
        : Array.isArray(d)
        ? d.map((x) => (x?.msg || x?.message || JSON.stringify(x))).join(", ")
        : (d && typeof d === "object" ? (d.msg || JSON.stringify(d)) : tx("errGeneric"));
      setErr(msg);
      setStatus("error");
    }
  };

  const reset = () => {
    setForm({ name: "", restaurant_name: "", email: "", message: "", website: "" });
    setStatus("idle");
    setErr("");
  };

  return (
    <div className="mx-auto max-w-2xl" data-testid="contact-form-wrap">
      {status === "success" ? (
        <div className="rounded-3xl border border-emerald-500/30 bg-gradient-to-br from-emerald-500/10 via-white/5 to-white/0 dark:from-emerald-500/15 dark:via-zinc-900 dark:to-zinc-950 p-8 sm:p-10 text-center" data-testid="contact-success">
          <div className="inline-flex w-14 h-14 rounded-2xl bg-emerald-500/15 text-emerald-500 items-center justify-center mx-auto">
            <CheckCircle2 className="h-7 w-7" />
          </div>
          <h3 className="mt-5 text-2xl font-bold tracking-tight">{tx("success")}</h3>
          <p className="mt-2 text-sm text-zinc-500 dark:text-zinc-400 leading-relaxed">{tx("successSub")}</p>
          <button
            onClick={reset}
            className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-zinc-300 dark:border-zinc-700 text-zinc-700 dark:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800 font-semibold text-sm transition-colors"
            data-testid="contact-reset-btn"
          >
            {tx("another")} →
          </button>
        </div>
      ) : (
        <form
          onSubmit={submit}
          className="rounded-3xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 p-6 sm:p-8 space-y-5 shadow-xl shadow-purple-500/5"
          data-testid="contact-form"
        >
          {/* Honeypot — invisible to humans */}
          <input
            type="text"
            name="website"
            value={form.website}
            onChange={change("website")}
            tabIndex={-1}
            autoComplete="off"
            className="absolute -left-[9999px] opacity-0 pointer-events-none"
            aria-hidden="true"
          />

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label={tx("name")} required>
              <input
                type="text"
                value={form.name}
                onChange={change("name")}
                placeholder={tx("placeholderName")}
                required
                maxLength={120}
                className="contact-input"
                data-testid="contact-input-name"
              />
            </Field>
            <Field label={tx("restaurant")}>
              <input
                type="text"
                value={form.restaurant_name}
                onChange={change("restaurant_name")}
                placeholder={tx("placeholderRestaurant")}
                maxLength={120}
                className="contact-input"
                data-testid="contact-input-restaurant"
              />
            </Field>
          </div>

          <Field label={tx("email")} required>
            <input
              type="email"
              value={form.email}
              onChange={change("email")}
              placeholder={tx("placeholderEmail")}
              required
              className="contact-input"
              data-testid="contact-input-email"
            />
          </Field>

          <Field label={tx("message")} required>
            <textarea
              value={form.message}
              onChange={change("message")}
              placeholder={tx("placeholderMessage")}
              required
              minLength={5}
              maxLength={4000}
              rows={5}
              className="contact-input resize-none"
              data-testid="contact-input-message"
            />
          </Field>

          {status === "error" && (
            <div className="flex items-start gap-2 p-3 rounded-xl bg-rose-500/10 border border-rose-500/30 text-rose-600 dark:text-rose-300 text-sm" data-testid="contact-error">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{err}</span>
            </div>
          )}

          <div className="flex flex-col-reverse sm:flex-row gap-3 sm:items-center sm:justify-between pt-2">
            <p className="text-xs text-zinc-500 dark:text-zinc-400">{tx("sub")}</p>
            <button
              type="submit"
              disabled={status === "sending"}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-purple-600 to-fuchsia-600 hover:from-purple-500 hover:to-fuchsia-500 disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold text-sm shadow-lg shadow-purple-600/30 transition-all hover:scale-[1.02] active:scale-100"
              data-testid="contact-submit-btn"
            >
              <Send className={`h-4 w-4 ${status === "sending" ? "animate-pulse" : ""}`} />
              {status === "sending" ? tx("sending") : tx("submit")}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-widest font-bold text-zinc-500 dark:text-zinc-400 mb-1.5">
        {label}
        {required && <span className="text-purple-500 ml-0.5">*</span>}
      </span>
      {children}
    </label>
  );
}
